import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MarkdownRenderer from "./MarkdownRenderer";

/**
 * Renders agent markdown with support for collapsible detail sections.
 *
 * The agent wraps long explanations in a fenced block:
 *
 *   :::detalhes Título opcional
 *   ...conteúdo longo em markdown...
 *   :::
 *
 * Everything outside the fences renders normally; each fenced block becomes
 * a hide/show toggle, collapsed by default. An unclosed fence (mid-stream)
 * is treated as a detail block so long content never flashes open while
 * streaming.
 */

const DETAILS_OPEN_RE = /^:::detalhes(?:\s+(.*))?$/;
const DETAILS_CLOSE = ":::";
// MarkdownRenderer has no fenced-code support, so a bare ``` line would
// render as literal backticks — models sometimes wrap the :::detalhes block
// or tables in fences despite instructions. Drop those lines entirely.
const CODE_FENCE_RE = /^```[\w-]*$/;

export function splitDetailSections(source) {
    let segments = [];
    let lines = (source || "").replace(/\r\n/g, "\n").split("\n");
    let current = [];
    let inDetails = false;
    let title = null;

    let flush = (type) => {
        let content = current.join("\n");
        if (content.trim() !== "") {
            segments.push({ type, content, title: type === "details" ? title : null });
        }
        current = [];
    };

    for (let line of lines) {
        let trimmed = line.trim();
        if (CODE_FENCE_RE.test(trimmed)) continue;
        if (!inDetails) {
            let open = trimmed.match(DETAILS_OPEN_RE);
            if (open) {
                flush("text");
                inDetails = true;
                title = (open[1] || "").trim() || null;
                continue;
            }
            current.push(line);
        } else {
            if (trimmed === DETAILS_CLOSE) {
                flush("details");
                inDetails = false;
                title = null;
                continue;
            }
            current.push(line);
        }
    }
    flush(inDetails ? "details" : "text");
    return segments;
}

function CollapsibleSection({ title, content }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <div className="qia-details-block">
            <div
                className="qia-details-toggle"
                onClick={() => setOpen(!open)}
                role="button"
            >
                <span className={"qia-details-chevron" + (open ? " qia-details-chevron-open" : "")}>▸</span>
                <span>{title || t("Explicação detalhada")}</span>
            </div>
            {open &&
                <div className="qia-details-content">
                    <MarkdownRenderer source={content} />
                </div>
            }
        </div>
    );
}

function CollapsibleMarkdown({ source }) {
    if (!source) return null;
    let segments = splitDetailSections(source);
    return (
        <div>
            {segments.map((segment, index) =>
                segment.type === "details"
                    ? <CollapsibleSection key={index} title={segment.title} content={segment.content} />
                    : <MarkdownRenderer key={index} source={segment.content} />
            )}
        </div>
    );
}

export default CollapsibleMarkdown;
